/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initAttr = {
  id: '',
  code: '',
  customer_id: '',
  customer_name: '',
  branch_id: '',
  name: '',
  vat_type: 'NO',
  revise: 'NO',
  remark: '',
  total: '',
  vat: '',
  nettotal: '',
  status: 'edit',
  print_url: '',
  created_at: '',
  updated_at: '',
  date_quotation: '',
};

function QuotationOpen({ parent }) {
  const mod = 'quotation';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const open = () => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/revise`, app.token, {
      id: parent.id,
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setLoading(false);

        setAttr(result.data);
        setList(result.list);
        setModalOpen(true);
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderList = () => {
    return (
      <TableContainer sx={{ boxShadow: 0, minHeight: 300 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align='center' width={20}>
                No.
              </TableCell>
              <TableCell align='center' width={40}>
                รูป
              </TableCell>
              <TableCell>สินค้า</TableCell>
              <TableCell align='right' width={90} className='nowrap'>
                จำนวน
              </TableCell>
              <TableCell align='right' width={90} className='nowrap'>
                ราคา
              </TableCell>
              <TableCell align='right' width={90} className='nowrap'>
                แถม
              </TableCell>
              <TableCell align='right' width={120} className='nowrap'>
                ลด STEP 1
              </TableCell>
              <TableCell align='right' width={120} className='nowrap'>
                ลด STEP 2
              </TableCell>
              <TableCell align='right' width={90}>
                ก่อนลด
              </TableCell>
              <TableCell align='right' width={90}>
                ส่วนลด
              </TableCell>
              <TableCell align='right' width={90}>
                รวม
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((row, i) => (
              <TableRow key={row.id}>
                <TableCell align='center'>{i + 1}</TableCell>
                <TableCell align='center'>{Func.imageDisplay(row)}</TableCell>
                <TableCell>
                  <MDBox className='nowrap'>
                    {row.code} <span className='color-default'>{row.sku}</span>
                  </MDBox>
                  <MDBox className='cut' width={250}>
                    {row.name}
                  </MDBox>
                </TableCell>
                <TableCell align='right'>
                  <MDBox className='color-primary'>{Func.numberDisplay(row.quantity)}</MDBox>
                  <MDBox>{row.unit_name}</MDBox>
                </TableCell>
                <TableCell align='right'>{row.price}</TableCell>
                <TableCell align='right'>
                  <MDBox className='color-primary'>
                    {row.quantity_free > 0 ? Func.numberDisplay(row.quantity_free) : '-'}
                  </MDBox>
                  <MDBox>{row.unit_name}</MDBox>
                </TableCell>
                <TableCell align='right' className='nowrap'>
                  {row.discount_value > 0 ? (
                    <span>
                      {Func.numberDisplay(row.discount_value)}{' '}
                      {row.discount_type === 'P' ? '%' : '฿'}
                    </span>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell align='right' className='nowrap'>
                  {row.discount_value2 > 0 ? (
                    <span>
                      {Func.numberDisplay(row.discount_value2)}{' '}
                      {row.discount_type2 === 'P' ? '%' : '฿'}
                    </span>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell align='right'>{Func.numberDisplay(row.total, 2)}</TableCell>
                <TableCell align='right'>
                  {row.discount > 0 ? Func.numberDisplay(row.discount, 2) : '-'}
                </TableCell>
                <TableCell align='right'>{Func.numberDisplay(row.amount, 2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderSummary = () => {
    return (
      <MDBox mt={1}>
        <Table aria-label='summary table'>
          <TableBody>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>มูลค่าสินค้า</MDTypography>
              </TableCell>
              <TableCell align='right' width='200'>
                <MDTypography variant='h5'>{Func.numberDisplay(attr.total, 2)}</MDTypography>
              </TableCell>
              <TableCell align='right' width={50}>
                <MDTypography variant='h5'>บาท</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>หักส่วนลด</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>
                  {Func.numberDisplay(attr.discount_value, 2)}
                </MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>บาท</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>หักส่วนลด</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>
                  {Func.numberDisplay(attr.discount_percent, 2)}
                </MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>%</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>มูลค่าสินค้าหลังหักส่วนลด</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>{Func.numberDisplay(attr.amount, 2)}</MDTypography>
              </TableCell>
              <TableCell align='right' width={50}>
                <MDTypography variant='h5'>บาท</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>
                  ภาษีมูลค่าเพิ่ม {Func.numberDisplay(attr.vat_value, 0)}%
                </MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>{Func.numberDisplay(attr.vat, 2)}</MDTypography>
              </TableCell>
              <TableCell align='right' width={50}>
                <MDTypography variant='h5'>บาท</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>มูลค่ารวมภาษี</MDTypography>
              </TableCell>
              <TableCell align='right' width='200'>
                <MDTypography variant='h5' color='primary'>
                  {Func.numberDisplay(attr.amount_vat, 2)}
                </MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>บาท</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>ค่าจัดส่ง</MDTypography>
              </TableCell>
              <TableCell align='right' width='200'>
                <MDTypography variant='h5'>{Func.numberDisplay(attr.shipping, 2)}</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>บาท</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>รวมทั้งสิ้น</MDTypography>
              </TableCell>
              <TableCell align='right' width='200'>
                <MDTypography variant='h5' color='info'>
                  {Func.numberDisplay(attr.nettotal, 2)}
                </MDTypography>
              </TableCell>
              <TableCell align='right' width={50}>
                <MDTypography variant='h5'>บาท</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>มัดจำ</MDTypography>
              </TableCell>
              <TableCell align='right' width='200'>
                <MDTypography variant='h5'>{Func.numberDisplay(attr.deposit, 2)}</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>บาท</MDTypography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </MDBox>
    );
  };

  const btnOpen = () => {
    return (
      <MDButton
        variant='text'
        tabIndex={-1}
        color='info'
        size='small'
        onClick={open}
        sx={{ fontSize: '1rem' }}
      >
        Rev. {parent.revise}
      </MDButton>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle>
          ใบเสนอราคา Revise. {parent.revise}
          <IconButton
            aria-label='close'
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {renderList()}
          {renderSummary()}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
      {btnOpen()}
      {renderOpen()}

      {renderLoading()}
    </div>
  );
}

QuotationOpen.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    revise: PropTypes.string.isRequired,
  }).isRequired,
};

export default QuotationOpen;
